// CertificateProgressCard — the student dashboard's per-course attendance
// progress + certificate status card.
import Badge from "../shared/Badge.js";
import { IconAward, IconCertificate } from "../shared/icons.js";
import type { CourseProgress } from "../../lib/studentDashboardTypes.js";

interface CertificateProgressCardProps {
  progress: CourseProgress[];
  isLoading: boolean;
}

export default function CertificateProgressCard({ progress, isLoading }: CertificateProgressCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-medium text-dm-navy">
        <IconAward width={16} height={16} className="text-dm-slate" />
        Your progress
      </h2>

      {isLoading && <p className="text-sm text-dm-slate">Loading…</p>}

      {!isLoading && progress.length === 0 && (
        <p className="text-sm text-dm-slate">Enroll in a course to start tracking progress.</p>
      )}

      {!isLoading && progress.length > 0 && (
        <div className="divide-y divide-gray-100">
          {progress.map((item) => {
            const percent =
              item.totalSessions > 0 ? Math.round((item.attendedSessions / item.totalSessions) * 100) : 0;
            return (
              <div key={item.courseId} className="py-3">
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <p className="truncate text-sm font-medium text-dm-navy">{item.courseTitle}</p>
                  {item.certificateIssued ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                      <IconCertificate width={12} height={12} />
                      Certificate issued
                    </span>
                  ) : item.completedAt ? (
                    <Badge tone="gold">Awaiting certificate</Badge>
                  ) : null}
                </div>
                <p className="mb-1 text-xs text-dm-slate">
                  {item.attendedSessions} of {item.totalSessions} sessions
                </p>
                <div className="h-2 w-full rounded-full bg-gray-100">
                  <div className="h-2 rounded-full bg-dm-royal transition-all" style={{ width: `${percent}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
